import React, { useState, useRef, useEffect } from 'react'

interface ColumnHeaderProps {
  label: string
  width?: number
  onResize?: (newWidth: number) => void
  isSortable?: boolean
  sortOrder?: 'asc' | 'desc' | null
  onSort?: () => void
}

export const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  label,
  width,
  onResize,
  isSortable = false,
  sortOrder = null,
  onSort
}) => {
  const [isResizing, setIsResizing] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current
      const newWidth = Math.max(50, (startWidthRef.current || 100) + delta)
      onResize?.(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, onResize])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    startXRef.current = e.clientX
    startWidthRef.current = width || 100
  }

  return (
    <div
      ref={headerRef}
      className="relative h-full flex items-center px-3 py-3 font-semibold text-xs text-slate-500 uppercase tracking-wider select-none bg-slate-50 border-r border-slate-200 w-full"
    >
      <div className="flex items-center gap-1 w-full overflow-hidden">
        <span className="truncate cursor-default flex-1">{label}</span>

        {isSortable && (
          <button
            onClick={onSort}
            className="ml-1 text-slate-400 hover:text-slate-600 transition-colors"
            title="Click to sort"
          >
            {sortOrder === 'asc' && '↑'}
            {sortOrder === 'desc' && '↓'}
            {sortOrder === null && '↕'}
          </button>
        )}
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        className={`absolute right-0 top-0 w-1 h-full cursor-col-resize hover:bg-primary-400 transition-colors z-10 ${
          isResizing ? 'bg-primary-500' : 'bg-transparent'
        }`}
        title="Drag to resize"
      />
    </div>
  )
}

